/**
 * 平面圖管理系統 - 真實 API 服務
 * 連接後端 API，替代 Mock 服務
 */

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, of, delay } from 'rxjs';
import { environment } from '../../../services/global-environment';
import { I18nService } from '../../../services/i18n.service';
import { 
  FloorplanMaster, 
  ApiResponse,
  IconDefinition
} from '../interfaces/floorplan.interface';

export interface FloorplanListParams {
  building_name?: string;
  floor_level?: string;
  page?: number;
  limit?: number;
  search?: string;
  is_active?: string;
}

export interface FloorplanListResponse {
  floorplans: FloorplanMaster[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

export interface FloorplanStats {
  total_floorplans: number;
  active_floorplans: number;
  total_buildings: number;
  total_floors: number;
}

@Injectable({
  providedIn: 'root'
})
export class FloorplanService {
  private apiUrl = environment.apiUrl; // http://localhost:8080/api
  
  // 快取資料
  private floorplansCache = new BehaviorSubject<FloorplanMaster[]>([]);
  
  constructor(
    private http: HttpClient,
    private i18nService: I18nService
  ) {
    console.log('🔗 真實 API 服務已啟動 - 平面圖管理系統');
    console.log('📡 API 端點:', `${this.apiUrl}/api/floorplan`);
    console.log('🎨 圖示功能使用內建生成器');
  }

  /**
   * Create new floorplan
   * POST /api/floorplan/create
   */
  createFloorplan(floorplanData: Omit<FloorplanMaster, 'id' | 'companyid' | 'createdAt' | 'updatedAt'>): Observable<ApiResponse<FloorplanMaster>> {
    return this.http.post<ApiResponse<FloorplanMaster>>(`${this.apiUrl}/api/floorplan/create`, floorplanData);
  }

  /**
   * 取得平面圖列表
   * GET /api/floorplan/list
   */
  getFloorplanList(params: FloorplanListParams): Observable<ApiResponse<FloorplanListResponse>> {
    let httpParams = new HttpParams();
    
    // 加入查詢參數
    Object.keys(params).forEach(key => {
      const value = (params as any)[key];
      if (value !== undefined && value !== null && value !== '') {
        httpParams = httpParams.set(key, value.toString());
      }
    });

    return this.http.get<ApiResponse<FloorplanListResponse>>(`${this.apiUrl}/api/floorplan/list`, { params: httpParams });
  }

  /**
   * Get single floorplan
   * GET /api/floorplan/:id
   */
  getFloorplan(id: string): Observable<ApiResponse<FloorplanMaster>> {
    return this.http.get<ApiResponse<FloorplanMaster>>(`${this.apiUrl}/api/floorplan/${id}`);
  }

  /**
   * Update floorplan
   * PUT /api/floorplan/:id
   */
  updateFloorplan(id: string, floorplanData: Partial<FloorplanMaster>): Observable<ApiResponse<FloorplanMaster>> {
    return this.http.put<ApiResponse<FloorplanMaster>>(`${this.apiUrl}/api/floorplan/${id}`, floorplanData);
  }

  /**
   * Delete floorplan (soft delete)
   * DELETE /api/floorplan/:id
   */
  deleteFloorplan(id: string): Observable<ApiResponse<any>> {
    return this.http.delete<ApiResponse<any>>(`${this.apiUrl}/api/floorplan/${id}`);
  }

  /**
   * Get building list
   * GET /api/floorplan/meta/buildings
   */
  getBuildings(): Observable<ApiResponse<string[]>> {
    return this.http.get<ApiResponse<string[]>>(`${this.apiUrl}/api/floorplan/meta/buildings`);
  }

  /**
   * Get floor list
   * GET /api/floorplan/meta/floors
   */
  getFloors(building_name?: string): Observable<ApiResponse<string[]>> {
    let params = new HttpParams();
    if (building_name) {
      params = params.set('building_name', building_name);
    }
    return this.http.get<ApiResponse<string[]>>(`${this.apiUrl}/api/floorplan/meta/floors`, { params });
  }

  /**
   * 取得統計資訊
   * GET /api/floorplan/stats
   */
  getStats(): Observable<ApiResponse<FloorplanStats>> {
    return this.http.get<ApiResponse<FloorplanStats>>(`${this.apiUrl}/api/floorplan/stats`);
  }

  /**
   * 取得快取的平面圖列表 (用於狀態管理)
   */
  get floorplans$(): Observable<FloorplanMaster[]> {
    return this.floorplansCache.asObservable();
  }

  /**
   * 更新快取
   */
  updateCache(floorplans: FloorplanMaster[]): void {
    this.floorplansCache.next(floorplans);
  }

  /**
   * 清除快取
   */
  clearCache(): void {
    this.floorplansCache.next([]);
  }

  /**
   * Save newly created floorplan (used by editor)
   * Save temporary floorplan to database
   */
  saveNewFloorplan(floorplanData: FloorplanMaster): Observable<ApiResponse<FloorplanMaster>> {
    // Extract required fields from complete floorplan data
    const payload = {
      created_by: floorplanData.created_by,
      name: floorplanData.name,
      description: floorplanData.description,
      floor_level: floorplanData.floor_level,
      building_name: floorplanData.building_name,
      file_url: floorplanData.file_url,
      file_type: floorplanData.file_type,
      file_size: floorplanData.file_size,
      scale_ratio: floorplanData.scale_ratio,
      scale_unit: floorplanData.scale_unit,
      width_pixels: floorplanData.width_pixels,
      height_pixels: floorplanData.height_pixels,
      real_width: floorplanData.real_width,
      real_height: floorplanData.real_height,
      is_active: floorplanData.is_active
    };

    return this.http.post<ApiResponse<FloorplanMaster>>(`${this.apiUrl}/api/floorplan/create`, payload);
  }

  /**
   * 儲存完整平面圖（包含所有圖層、區域、圖示等）
   * POST /api/floorplan/save-complete
   */
  saveCompleteFloorplan(completeFloorplanData: any): Observable<ApiResponse<any>> {
    console.log('🚀 真實 API 服務：儲存完整平面圖', completeFloorplanData);
    return this.http.post<ApiResponse<any>>(`${this.apiUrl}/api/floorplan/save-complete`, completeFloorplanData);
  }

  /**
   * Load complete floorplan data (including all layers, regions, icons, etc.)
   * GET /api/floorplan/load-complete/:id
   */
  loadCompleteFloorplan(id: string): Observable<ApiResponse<any>> {
    console.log('🔍 Real API Service: Load complete floorplan', { id });
    return this.http.get<ApiResponse<any>>(`${this.apiUrl}/api/floorplan/load-complete/${id}`);
  }

  /**
   * Get icon definitions
   * Use built-in icon generator, load icons from local assets
   */
  getIconDefinitions(): Observable<ApiResponse<any>> {
    console.log('🎨 Load icon definitions - Using built-in generator');
    
    // Generate icon definitions dynamically
    const iconDefinitions = this.generateIconDefinitionsFromFiles();
    
    let definitions = iconDefinitions.filter(def => def.is_default);
    
    // Category statistics
    const categories = Array.from(new Set(definitions.map(def => def.category)));
    
    const response = {
      success: true,
      data: {
        icon_definitions: definitions,
        categories,
        pagination: {
          current_page: 1,
          total_pages: 1,
          total_count: definitions.length,
          has_next: false,
          has_previous: false
        }
      },
      timestamp: new Date().toISOString()
    };
    
    return of(response).pipe(delay(200)); // Simulate slight delay
  }

  /**
   * 從真實圖示文件生成圖示定義
   */
  private generateIconDefinitionsFromFiles(): IconDefinition[] {
    const iconDefinitions: IconDefinition[] = [];
    
    // GHS 危害標示
    const ghsIcons = [
      { file: 'ghs01-explosive.svg', name: this.i18nService.translate('floorplan.icons.ghs01.name'), description: this.i18nService.translate('floorplan.icons.ghs01.description'), tags: ['GHS', 'explosive', 'hazard'] },
      { file: 'ghs02-flammable.svg', name: this.i18nService.translate('floorplan.icons.ghs02.name'), description: this.i18nService.translate('floorplan.icons.ghs02.description'), tags: ['GHS', 'flammable', 'hazard'] },
      { file: 'ghs03-oxidizing.svg', name: this.i18nService.translate('floorplan.icons.ghs03.name'), description: this.i18nService.translate('floorplan.icons.ghs03.description'), tags: ['GHS', 'oxidizing', 'hazard'] },
      { file: 'ghs04-compressed-gas.svg', name: this.i18nService.translate('floorplan.icons.ghs04.name'), description: this.i18nService.translate('floorplan.icons.ghs04.description'), tags: ['GHS', 'compressed-gas', 'hazard'] },
      { file: 'ghs05-corrosive.svg', name: this.i18nService.translate('floorplan.icons.ghs05.name'), description: this.i18nService.translate('floorplan.icons.ghs05.description'), tags: ['GHS', 'corrosive', 'hazard'] },
      { file: 'ghs06-acute-toxicity.svg', name: this.i18nService.translate('floorplan.icons.ghs06.name'), description: this.i18nService.translate('floorplan.icons.ghs06.description'), tags: ['GHS', 'acute-toxicity', 'hazard'] },
      { file: 'ghs07-harmful.svg', name: this.i18nService.translate('floorplan.icons.ghs07.name'), description: this.i18nService.translate('floorplan.icons.ghs07.description'), tags: ['GHS', 'harmful', 'hazard'] },
      { file: 'ghs08-health-hazard.svg', name: this.i18nService.translate('floorplan.icons.ghs08.name'), description: this.i18nService.translate('floorplan.icons.ghs08.description'), tags: ['GHS', 'health-hazard', 'hazard'] },
      { file: 'ghs09-environmental-hazard.svg', name: this.i18nService.translate('floorplan.icons.ghs09.name'), description: this.i18nService.translate('floorplan.icons.ghs09.description'), tags: ['GHS', 'environmental-hazard', 'hazard'] }
    ];
    
    // 火災保護
    const fireProtectionIcons = [
      { file: 'dry-powder-extinguisher.svg', name: '乾粉滅火器', description: '乾粉式滅火器', tags: ['滅火器', '乾粉', '消防'] },
      { file: 'co2-extinguisher.svg', name: '二氧化碳滅火器', description: 'CO2 氣體滅火器', tags: ['滅火器', 'CO2', '消防'] },
      { file: 'foam-extinguisher.svg', name: '泡沫滅火器', description: '泡沫式滅火器', tags: ['滅火器', '泡沫', '消防'] },
      { file: 'fire-hose-reel.svg', name: '消防栓', description: '消防水帶捲盤', tags: ['消防栓', '水帶', '消防'] },
      { file: 'fire-alarm-call-point.svg', name: '火災警報按鈕', description: '手動火災警報按鈕', tags: ['火災', '警報', '按鈕'] },
      { file: 'fire-door.svg', name: '防火門', description: '防火安全門', tags: ['防火門', '安全', '逃生'] },
      { file: 'fire-ladder.svg', name: '消防梯', description: '緊急逃生梯', tags: ['消防梯', '逃生', '梯子'] },
      { file: 'fire-emergency-phone.svg', name: '緊急通話', description: '火災緊急電話', tags: ['緊急電話', '通訊', '火災'] }
    ];
    
    // 安全狀況
    const safetyStatusIcons = [
      { file: 'emergency-shower.svg', name: '緊急沖淋設備', description: '化學品沖洗緊急淋浴', tags: ['沖淋', '緊急', '化學品'] },
      { file: 'eyewash-station.svg', name: '洗眼器', description: '緊急洗眼設備', tags: ['洗眼器', '緊急', '眼部'] },
      { file: 'first-aid.svg', name: '急救設備', description: '急救箱或急救設備', tags: ['急救', '醫療', '安全'] },
      { file: 'aed-defibrillator.svg', name: 'AED除顫器', description: '自動體外心臟除顫器', tags: ['AED', '除顫器', '心臟'] },
      { file: 'stretcher.svg', name: '擔架', description: '緊急救護擔架', tags: ['擔架', '救護', '緊急'] },
      { file: 'doctor.svg', name: '醫護人員', description: '醫護人員位置', tags: ['醫生', '醫護', '人員'] },
      { file: 'emergency-phone.svg', name: '緊急電話', description: '緊急求救電話', tags: ['緊急電話', '求救', '通訊'] },
      { file: 'assembly-point.svg', name: '集合點', description: '緊急疏散集合點', tags: ['集合點', '疏散', '緊急'] },
      { file: 'break-glass.svg', name: '破玻璃求救', description: '緊急破玻璃警報', tags: ['破玻璃', '警報', '緊急'] },
      { file: 'elevator.svg', name: '電梯', description: '升降梯位置', tags: ['電梯', '升降', '設施'] },
      { file: 'rescue-window.svg', name: '救生窗', description: '緊急救生窗口', tags: ['救生窗', '逃生', '窗口'] },
      { file: 'emergency-exit-left.svg', name: '緊急出口(左)', description: '向左方向的緊急出口', tags: ['緊急出口', '逃生', '左'] },
      { file: 'emergency-exit-right.svg', name: '緊急出口(右)', description: '向右方向的緊急出口', tags: ['緊急出口', '逃生', '右'] },
      { file: 'emergency-exit-window-with-ladder.svg', name: '逃生窗梯', description: '緊急逃生窗及梯子', tags: ['逃生窗', '梯子', '緊急'] },
      { file: 'directional-arrow-45deg.svg', name: '方向指示(45°)', description: '45度角方向指示箭頭', tags: ['方向', '箭頭', '45度'] },
      { file: 'directional-arrow-90deg.svg', name: '方向指示(90°)', description: '90度角方向指示箭頭', tags: ['方向', '箭頭', '90度'] }
    ];
    
    // 設備設施
    const facilityEquipmentIcons = [
      { file: 'radioactive-material.svg', name: '放射性物質', description: '放射性物質或電離輻射設備', tags: ['放射性', '輻射', '危險'] },
      { file: 'generator.svg', name: '發電機', description: '緊急發電機設備', tags: ['發電機', '電力', '緊急'] },
      { file: 'high-voltage.svg', name: '高壓電設備', description: '高壓電力設備警告標誌', tags: ['高壓電', '危險', '電力'] }
    ];
    
    // 公共危險物品
    const publicHazardIcons = [
      { file: 'class1-oxidizing-solid.svg', name: '第1類-氧化性固體', description: '第1類公共危險物品 - 氧化性固體', tags: ['第1類', '氧化性', '固體'] },
      { file: 'class2-flammable-solid.svg', name: '第2類-可燃性固體', description: '第2類公共危險物品 - 可燃性固體', tags: ['第2類', '可燃性', '固體'] },
      { file: 'class3-pyrophoric-water-reactive.svg', name: '第3類-自燃性/禁水性物質', description: '第3類公共危險物品 - 自燃性及禁水性物質', tags: ['第3類', '自燃性', '禁水性'] },
      { file: 'class4-flammable-combustible-liquid.svg', name: '第4類-易燃液體', description: '第4類公共危險物品 - 易燃液體', tags: ['第4類', '易燃', '液體'] },
      { file: 'class5-self-reactive-organic-peroxide.svg', name: '第5類-自反應性/有機過氧化物', description: '第5類公共危險物品 - 自反應性物質及有機過氧化物', tags: ['第5類', '自反應性', '有機過氧化物'] },
      { file: 'class6-oxidizing-liquid.svg', name: '第6類-氧化性液體', description: '第6類公共危險物品 - 氧化性液體', tags: ['第6類', '氧化性', '液體'] }
    ];
    
    // 生成圖示定義
    let idCounter = 1;
    
    // GHS 危害標示
    ghsIcons.forEach(icon => {
      iconDefinitions.push({
        id: `ghs_${idCounter.toString().padStart(3, '0')}`,
        companyid: 'system',
        created_by: 'system',
        name: icon.name,
        category: 'ghs_hazard_symbols',
        subcategory: this.i18nService.translate('floorplan.icons.ghs_subcategory'),
        description: icon.description,
        icon_url: `/assets/icons/ghs-hazard-symbols/${icon.file}`,
        is_default: true,
        is_active: true,
        default_size: { width: 32, height: 32 },
        tags: icon.tags,
        usage_count: Math.floor(Math.random() * 20) + 1,
        createdAt: '2025-09-04T10:00:00Z',
        updatedAt: '2025-09-04T10:00:00Z'
      });
      idCounter++;
    });
    
    // 火災保護
    fireProtectionIcons.forEach(icon => {
      iconDefinitions.push({
        id: `fire_${idCounter.toString().padStart(3, '0')}`,
        companyid: 'system',
        created_by: 'system',
        name: icon.name,
        category: 'fire_protection',
        subcategory: '消防設備',
        description: icon.description,
        icon_url: `/assets/icons/fire-protection/${icon.file}`,
        is_default: true,
        is_active: true,
        default_size: { width: 32, height: 32 },
        tags: icon.tags,
        usage_count: Math.floor(Math.random() * 20) + 1,
        createdAt: '2025-09-04T10:00:00Z',
        updatedAt: '2025-09-04T10:00:00Z'
      });
      idCounter++;
    });
    
    // 安全狀況
    safetyStatusIcons.forEach(icon => {
      iconDefinitions.push({
        id: `safety_${idCounter.toString().padStart(3, '0')}`,
        companyid: 'system',
        created_by: 'system',
        name: icon.name,
        category: 'safety_status',
        subcategory: '安全設備',
        description: icon.description,
        icon_url: `/assets/icons/safety-status/${icon.file}`,
        is_default: true,
        is_active: true,
        default_size: { width: 32, height: 32 },
        tags: icon.tags,
        usage_count: Math.floor(Math.random() * 20) + 1,
        createdAt: '2025-09-04T10:00:00Z',
        updatedAt: '2025-09-04T10:00:00Z'
      });
      idCounter++;
    });
    
    // 設備設施
    facilityEquipmentIcons.forEach(icon => {
      iconDefinitions.push({
        id: `facility_${idCounter.toString().padStart(3, '0')}`,
        companyid: 'system',
        created_by: 'system',
        name: icon.name,
        category: 'facility_equipment',
        subcategory: '危險設備',
        description: icon.description,
        icon_url: `/assets/icons/facility-equipment/${icon.file}`,
        is_default: true,
        is_active: true,
        default_size: { width: 32, height: 32 },
        tags: icon.tags,
        usage_count: Math.floor(Math.random() * 20) + 1,
        createdAt: '2025-09-04T10:00:00Z',
        updatedAt: '2025-09-04T10:00:00Z'
      });
      idCounter++;
    });
    
    // 公共危險物品
    publicHazardIcons.forEach(icon => {
      iconDefinitions.push({
        id: `hazmat_${idCounter.toString().padStart(3, '0')}`,
        companyid: 'system',
        created_by: 'system',
        name: icon.name,
        category: 'public_hazard_materials',
        subcategory: '公共危險物品',
        description: icon.description,
        icon_url: `/assets/icons/public-hazard-materials/${icon.file}`,
        is_default: true,
        is_active: true,
        default_size: { width: 32, height: 32 },
        tags: icon.tags,
        usage_count: Math.floor(Math.random() * 20) + 1,
        createdAt: '2025-09-04T10:00:00Z',
        updatedAt: '2025-09-04T10:00:00Z'
      });
      idCounter++;
    });
    
    return iconDefinitions;
  }
}